// Exact ordered rendering for an already-safe assistant parts timeline. The transport/controller owns arrival
// order and correlation; this component only groups adjacent trace rows for the established visual treatment.

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ActivityItem,
  ActivityPartGroup,
  ReasoningSummaryDisclosure,
  ThoughtText,
} from './ChatActivity.tsx';
import { StreamingCaret } from './ChatConversation.tsx';
import { Icon } from './Icon.tsx';
import type { ChatTurnPart } from '../surfaces/chat-turns.ts';
import { collapseEmailAttempts } from '../surfaces/chat-email.ts';
import { foldTraceSummaries } from '../surfaces/chat-reasoning.ts';
import type { FoldedTraceEntry } from '../surfaces/chat-reasoning.ts';

type TracePart = Extract<ChatTurnPart, { readonly type: 'activity' | 'reasoning' }>;

type RenderGroup =
  | {
      readonly type: 'text';
      readonly part: Extract<ChatTurnPart, { readonly type: 'text' }>;
      readonly index: number;
    }
  | {
      readonly type: 'trace';
      readonly parts: readonly { readonly part: TracePart; readonly index: number }[];
    };

/** Group adjacent non-text rows without moving them across a text boundary. */
function renderGroups(parts: readonly ChatTurnPart[]): readonly RenderGroup[] {
  const groups: RenderGroup[] = [];
  parts.forEach((part, index) => {
    if (part.type === 'text') {
      groups.push({ type: 'text', part, index });
      return;
    }
    const last = groups[groups.length - 1];
    if (last?.type === 'trace') {
      groups[groups.length - 1] = { ...last, parts: [...last.parts, { part, index }] };
    } else {
      groups.push({ type: 'trace', parts: [{ part, index }] });
    }
  });
  return groups;
}

/** Render the one latest-wins reasoning sentence inside the trace treatment. */
function LiveReasoningRow({
  text,
  active,
}: {
  readonly text: string;
  readonly active: boolean;
}): React.JSX.Element {
  return (
    <div className="chat-tr-row chat-tr-think" data-chat-part="reasoning">
      <span className="tr-ico" aria-hidden="true">
        <Icon name="chat" size={12} />
      </span>
      <span className="tr-label">Reasoning</span>
      <span className="tr-think ritona-voice">
        <ThoughtText label={text} />
      </span>
      {active ? <StreamingCaret /> : null}
    </div>
  );
}

/** One trace part, before and after the email collapse has run over the group. */
type TracePartEntry = { readonly part: TracePart; readonly index: number };
type CollapsedPart = { readonly row: TracePartEntry; readonly refusedBefore: number };
type FoldedPart = FoldedTraceEntry<CollapsedPart>;

/** The stable render key for one folded entry — a disclosure keys off its first recorded thought. */
function traceKey(entry: FoldedPart, position: number): string {
  if (entry.type === 'summaries') return `thoughts-${entry.rows[0]?.id ?? String(position)}`;
  const { part, index } = entry.row.row;
  if (part.type === 'reasoning') return `reasoning-${String(index)}`;
  return part.activity.toolCallId ?? part.activity.id;
}

/** One folded entry: the thoughts disclosure, the live reasoning sentence, or an activity card. */
function TraceEntry({
  entry,
  active,
}: {
  readonly entry: FoldedPart;
  readonly active: boolean;
}): React.JSX.Element {
  if (entry.type === 'summaries') return <ReasoningSummaryDisclosure rows={entry.rows} />;
  const { part } = entry.row.row;
  if (part.type === 'reasoning') return <LiveReasoningRow text={part.text} active={active} />;
  return <ActivityItem row={part.activity} refusedBefore={entry.row.refusedBefore} />;
}

/**
 * One trace group's rows. Split out of {@link OrderedChatParts} for the function-length ceiling when the
 * email collapse landed.
 *
 * Consecutive email ATTEMPTS collapse to the one that matters before anything renders — see
 * {@link collapseEmailAttempts}. The trace keeps every attempt; the thread shows one card that says how
 * many it took.
 *
 * The reasoning summaries fold AFTERWARDS, into one quiet disclosure per adjacent run (owner, 11 Aug 2026).
 * The order of the two passes is load-bearing: email adjacency stays decided on the sequence the server
 * published, so lifting a thought out from between two attempts can never make them look consecutive.
 */
function TraceRows({
  parts,
  streaming,
  groupIsLast,
}: {
  readonly parts: readonly { readonly part: TracePart; readonly index: number }[];
  readonly streaming: boolean;
  readonly groupIsLast: boolean;
}): React.JSX.Element {
  const rows = collapseEmailAttempts(
    parts,
    (entry) =>
      entry.part.type === 'activity' &&
      (entry.part.activity.kind === 'email_sent' ||
        entry.part.activity.kind === 'email_send_requested'),
    (entry) => entry.part.type === 'activity' && entry.part.activity.outcome === 'ok',
  );
  const entries = foldTraceSummaries(rows, (entry) =>
    entry.row.part.type === 'activity' && entry.row.part.activity.kind === 'thinking_summary'
      ? entry.row.part.activity
      : null,
  );
  return (
    <>
      {entries.map((entry, traceIndex) => (
        <TraceEntry
          key={traceKey(entry, traceIndex)}
          entry={entry}
          active={streaming && groupIsLast && traceIndex === entries.length - 1}
        />
      ))}
    </>
  );
}

/** Render safe text, the single live reasoning sentence, and tool/activity cards in supplied order. */
export function OrderedChatParts({
  parts,
  streaming = false,
}: {
  readonly parts: readonly ChatTurnPart[];
  readonly streaming?: boolean;
}): React.JSX.Element | null {
  if (parts.length === 0) return null;
  const groups = renderGroups(parts);
  if (groups.length === 0) return null;
  return (
    <>
      {groups.map((group, groupIndex) => {
        const groupIsLast = groupIndex === groups.length - 1;
        if (group.type === 'text') {
          return (
            <div className="chat-md" data-chat-part="text" key={`text-${String(group.index)}`}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{group.part.text}</ReactMarkdown>
              {streaming && groupIsLast ? <StreamingCaret /> : null}
            </div>
          );
        }
        return (
          <ActivityPartGroup key={`trace-${String(group.parts[0]?.index ?? groupIndex)}`}>
            <TraceRows parts={group.parts} streaming={streaming} groupIsLast={groupIsLast} />
          </ActivityPartGroup>
        );
      })}
    </>
  );
}
